import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePostAdminUsers } from '@/__generated__/admin/admin';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { isAxiosError } from 'axios';

const formSchema = z.object({
  email: z.string().email('不正なメールアドレスです'),
});

type FormValues = z.infer<typeof formSchema>;

export function InviteAdminUserDialog({ onSuccess }: { onSuccess: () => void }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const { trigger: inviteAdminUser, reset } = usePostAdminUsers();

  const onSubmit = async (values: FormValues) => {
    try {
      await inviteAdminUser({
        email: values.email,
      });
      setIsOpen(false);
      toast({
        title: '招待しました',
        description: '招待メールを送信しました',
        variant: 'default',
      });
      reset();
      form.reset();
      onSuccess();
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          title: '招待に失敗しました',
          description: error.response?.data.message,
          variant: 'destructive',
        });
      }
      console.error('Failed to create admin user:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>招待</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>管理者ユーザー招待</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>メールアドレス</FormLabel>
                  <FormControl>
                    <Input placeholder="メールアドレスを入力" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit">招待</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
